import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { ReplaySubject, merge, of, takeUntil } from 'rxjs';
import { filter, pairwise, switchMap, tap } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { ModalErrorComponent, ModalErrorData } from 'app/shared/modal-error/modal-error.component';
import { SnackBarType, SnackBarData, SnackBarComponent } from 'app/shared/snackbar/snackbar.component';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import Social from 'entity/Social';
import User from 'entity/User';
import { SocialState } from 'messages/websocket.messages';
import {
  EndRecordingMessage,
  OutgoingMessageType,
  ProcessRecordingMessage,
  RecordingType,
  StartRecordingMessage,
  UpdateType,
} from 'messages/websocket.messages';
import CompanyController from 'rest/CompanyController';
import RecordingController from 'rest/RecordingController';

import {
  ModalRecordingNameComponent,
  ModalRecordingNameData,
} from '../../modal-recording-name/modal-recording-name.component';
import SocialStateHelper from '../../social-state-machine/social-state-helper';
import SocialStateMachine from '../../social-state-machine/social-state-machine';
import { socialAppSelectors } from '../../state/app';
import { RECORDING_STOP_FAILED_ERROR, RECORDING_START_FAILED_ERROR } from '../../strings';
import { HeaderToastMessageService } from '../header-toast-message.service';
import { RegionService } from '../region/region.service';
import { WebsocketService } from '../websocket.service';

import { createRecordingId } from './util';

@Injectable({
  providedIn: 'root',
})

/**
 * Keep all recording logic for the presentation recorder components
 */
export class PresentationRecorderService extends Loggable implements OnDestroy {
  public isHost = false;
  public isPresenter = false;

  public isCrossRoomPresentation = false;
  public isPresentationActive = false;
  public isPresentationAudioMixEnabled = false;
  public isRecording = false;
  public isUserPresentingAndRecording = false;

  public isRecordingEnabledFlag = false;
  public isRecordingDisabled = false;

  public recordingId?: string;
  private socialId?: string;
  private socialUserId?: string;
  public recordingAuthorId = '';
  private social?: Social;
  private user?: User;
  private userId = 'unknown';
  private isWideRecording = false;

  private aiSummaries = false;

  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor(
    private store: Store<AppState>,
    private headerToastMessageService: HeaderToastMessageService,
    private dialog: MatDialog,
    private websocketService: WebsocketService,
    private regionService: RegionService,
    private _snackBar: MatSnackBar,
  ) {
    super();
    this.setDisplayName('PresentationRecorderService');

    // User state
    this.store.select(coreSelectors.getUserEntity).subscribe((user) => {
      this.user = user;
      this.userId = user?.userId ?? 'unknown';
    });

    // Social state
    this.store.select(socialAppSelectors.getCurrentSocial).subscribe((social) => {
      this.social = social;
    });
    this.store.select(socialAppSelectors.getCurrentSocialId).subscribe((socialId) => (this.socialId = socialId));
    this.store.select(socialAppSelectors.getCurrentSocial).subscribe((social) => (this.socialUserId = social?.userId));
    this.store.select(socialAppSelectors.isHost).subscribe((isHost) => (this.isHost = isHost));

    this.store
      .select(socialAppSelectors.getSocialSharedState)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((state: SocialState) => {
        this.isPresenter = SocialStateHelper.isUserPresenter(state, this.user?.userId ?? '');
      });

    // Presentation State
    this.store.select(socialAppSelectors.getPresentation).subscribe((presentation) => {
      this.isPresentationActive = !!presentation;
    });

    this.store
      .select(socialAppSelectors.isUserPresentingAndRecorded)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isUserPresentingAndRecorded) => (this.isUserPresentingAndRecording = isUserPresentingAndRecorded));

    this.store
      .select(socialAppSelectors.isPresentationRecording)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isRecording) => {
        this.isRecording = isRecording;
      });

    this.store
      .select(socialAppSelectors.isCrossRoomPresentation)
      .subscribe((isCrossRoomPresentation) => (this.isCrossRoomPresentation = isCrossRoomPresentation));

    // Recording state
    this.store.select(socialAppSelectors.getRecordingId).subscribe((recordingId) => (this.recordingId = recordingId));
    this.store
      .select(socialAppSelectors.isPresentationRecording)
      .subscribe((isRecording) => (this.isRecording = isRecording));

    // Start and stop recording of the local presenter's publisher stream
    this.store
      .select(socialAppSelectors.isUserPresentingAndRecorded)
      .pipe(
        pairwise(),
        filter(([oldRecording, newRecording]) => oldRecording !== newRecording),
      )
      .subscribe(([_, recording]) => {
        if (recording) {
          this.startPresenterRecording();
        } else {
          this.endPresenterRecording();
        }
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.presentation_recording)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isRecordingEnabled) => (this.isRecordingEnabledFlag = isRecordingEnabled));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.presentation_audio_mix)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((presentation_audio_mix) => (this.isPresentationAudioMixEnabled = presentation_audio_mix));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.ai_summaries)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((aiSummaries) => (this.aiSummaries = aiSummaries));

    this.store
      .select(socialAppSelectors.isWideRecording)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isWideRecording) => {
        this.isWideRecording = isWideRecording;
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  // Recorder Handlers
  /**
   * Toggle  presentation recording for the room. Existing presenters will initiate recording of their streams.
   */
  public handleRecordPresentation(): void {
    this.logUserAction(this.displayName, !this.isRecording ? 'Start Recording' : 'End Recording');
    this.toggleRecording();
  }

  // Presentation Recording
  /**
   * Initiate a recording of the active presentation.
   * @param name The name of the recording.
   */
  public async recordPresentation(name: string): Promise<void> {
    if (!this.isPresentationActive) {
      this.error('recordPresentation: Cannot record presentation, presentation is not active');
      throw new Error('Presentation is not active');
    }

    if (!this.socialUserId) {
      this.error('recordPresentation: Cannot record presentation, socialUserId is undefined');
      throw new Error('socialUserId is undefined');
    }

    if (this.isRecording) {
      this.error('recordPresentation: Cannot record presentation, already recording');
      throw new Error('already recording');
    }

    const { companyId } = await new CompanyController().getUserCompany(this.socialUserId);
    if (!companyId) {
      this.error('Failed to retrieve company');
      throw new Error('Failed to retrieve company');
    }

    this.info('Recording presentation');

    const { socialId, userId } = this;
    if (!socialId) {
      throw new Error('socialId is undefined');
    }
    const recordingId = createRecordingId({ socialId, companyId });

    try {
      const recording = await new RecordingController().processRecording({
        recordingId,
        socialId: this.socialId,
        name,
      });
      this.recordingAuthorId = this.socialUserId;
      this.info('Recording created', { recording });
    } catch (error) {
      this.error('Error processing recording creation: ', error);
    }

    try {
      await SocialStateMachine.shared.requestUpdatePresentation({
        userId,
        updatedProperties: { recordingId, record: true },
        updateType: this.isCrossRoomPresentation ? UpdateType.ALL : UpdateType.CURRENT,
        operation: 'recordPresentation',
      });
    } catch (error) {
      this.error('Error updating state machine at - "recordPresentation": ', error);
    }

    this.startMixRecording(recordingId);
  }

  public async endRecording(): Promise<boolean> {
    const { recordingId } = this;
    if (!this.isRecording || !recordingId) {
      this.error('endRecording: Cannot end presentation recording, recording is not active');
      return false;
    }

    this.info('Ending presentation mix', { recordingId });

    this.endMixRecording();

    this.info('Ending presentation recording', { recordingId });

    const { userId } = this;
    try {
      await SocialStateMachine.shared.requestUpdatePresentation({
        userId,
        updatedProperties: { recordingId: undefined, record: false },
        updateType: this.isCrossRoomPresentation ? UpdateType.ALL : UpdateType.CURRENT,
        operation: 'endRecording',
      });
    } catch (error) {
      this.error('Error updating state machine at - "endRecording": ', error);
    }

    this.startRecordingProcessing(recordingId);

    this.recordingId = undefined;

    return true;
  }

  private get defaultRecordingName(): string {
    const socialName = this.social?.name ?? 'Room';
    return `${socialName} recording`;
  }

  public toggleRecording(): void {
    if (this.isWideRecording) {
      this.recordPresentation(this.defaultRecordingName)
        .then(() => {
          if (!this.aiSummaries) {
            this.openRecordingWarning('This Presentation is being recorded.', 'success', 'START_RECORDING_3');
          }
        })
        .catch((error) => this.error("Couldn't start presentation recording", error));
      return;
    }

    const enableRecording$ = of(this.isRecording ?? false).pipe(
      filter((isRecording: boolean) => !isRecording),
      tap(() => {
        this.isRecordingDisabled = true;
        this.logUserAction(this.displayName, 'Start Recording');
      }),
      switchMap(() =>
        this.dialog
          .open<ModalRecordingNameComponent, ModalRecordingNameData, string>(ModalRecordingNameComponent, {
            data: { defaultName: this.defaultRecordingName },
          })
          .afterClosed(),
      ),
      // If the user cancels, clear the disable flag.
      tap((res) => {
        if (!res) {
          this.isRecordingDisabled = false;
        }
      }),
      filter(notNil),
      switchMap((name) => this.recordPresentation(name)),
      tap(() => {
        if (!this.aiSummaries) {
          this.openRecordingWarning('This Presentation is being recorded.', 'success', 'START_RECORDING_3');
        }
      }),
    );

    const disableRecording$ = of(this.isRecording).pipe(
      filter((isRecording) => isRecording ?? false),
      tap(() => {
        this.isRecordingDisabled = true;
        this.logUserAction(this.displayName, 'Stop Recording');
      }),
      switchMap(() => this.endRecording()),
      tap(() => {
        this.headerToastMessageService.pushTemporaryMessage({
          message: 'Recording ended',
        });
      }),
    );

    merge(enableRecording$, disableRecording$).subscribe({
      next: () => {
        this.isRecordingDisabled = false;
      },
      error: (err: unknown) => {
        this.error("Couldn't toggle recording", err);
        const error = this.isRecording ? RECORDING_STOP_FAILED_ERROR : RECORDING_START_FAILED_ERROR;
        this.dialog.open<ModalErrorComponent, ModalErrorData>(ModalErrorComponent, {
          data: error,
          panelClass: 'error-modal',
        });
        this.isRecordingDisabled = false;
      },
    });
  }

  private openRecordingWarning(message: string, type?: SnackBarType, icon?: string, duration = 6000) {
    const _data: SnackBarData = {
      message: message,
      type: type || '',
      icon: icon || '',
      mode: 'dark',
    };
    this._snackBar.openFromComponent(SnackBarComponent, {
      duration: duration,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'custom-snackbar',
      data: _data,
    });
  }

  /**
   * Start recording of this presenters stream
   *
   * Recording is toggled for the room globally, but each client is responsible for initiating the recording of their own stream.
   */
  private startPresenterRecording() {
    if (!this.recordingId) {
      this.error('Attempted to start a presentation recording without recording id.');
      return;
    }
    this.info('Starting publisher recording', { recordingId: this.recordingId });
    const msg: StartRecordingMessage = {
      t: OutgoingMessageType.START_RECORDING,
      recordingId: this.recordingId,
      type: RecordingType.PUBLISHER,
    };
    this.websocketService.sendMessage(msg);
  }

  /**
   * Stop recording of this presenters stream
   *
   * Recording is toggled for the room globally, but each client is responsible for initiating the recording of their own stream.
   */
  private endPresenterRecording() {
    this.info('Stopping publisher recording', { recordingId: this.recordingId });
    const msg: EndRecordingMessage = {
      t: OutgoingMessageType.END_RECORDING,
      type: RecordingType.PUBLISHER,
    };
    this.websocketService.sendMessage(msg);
  }

  /**
   * Start recording of the audio mix
   *
   * Recording is toggled for the room globally. The client who initiated recording is responsible starting the mix recording.
   */
  public startMixRecording(recordingId: string) {
    if (!this.isPresentationAudioMixEnabled) {
      return;
    }
    this.info('Starting mix recording', { recordingId });
    const msg: StartRecordingMessage = {
      t: OutgoingMessageType.START_RECORDING,
      recordingId,
      type: RecordingType.AUDIO_MIX,
    };
    this.websocketService.sendMessage(msg);
  }

  /**
   * Stop recording of the audio mix.
   *
   * Recording is toggled for the room globally. The client who ended recording is responsible starting the mix recording.
   */
  public endMixRecording() {
    if (!this.isPresentationAudioMixEnabled) {
      return;
    }
    this.info('Stopping mix recording', { recordingId: this.recordingId });
    const msg: EndRecordingMessage = {
      t: OutgoingMessageType.END_RECORDING,
      type: RecordingType.AUDIO_MIX,
    };
    this.websocketService.sendMessage(msg);
  }

  /**
   * Initiate backend recording processing.
   * @param recordingId The recording to process
   */
  private startRecordingProcessing(recordingId: string) {
    this.info('Starting recording processing', { recordingId: this.recordingId });
    // Start presentation processing
    const processingRecordingMessage: ProcessRecordingMessage = {
      t: OutgoingMessageType.PROCESS_RECORDING,
      recordingId,
    };

    this.websocketService.sendMessage(processingRecordingMessage);
  }

  // Record visibility settings
  // Check if the user is the host/presenter and if the recording flag is enabled, to show the recording button in the presentation.
  public get isRecordingOptionVisible(): boolean {
    return this.isRecordingEnabledFlag && (this.isHost || this.isPresenter);
  }

  // Check if the region of the room has recording available, to enable/disable the recording options.
  public get isRecordingOptionRegionEnabled(): boolean {
    return this.isRecordingEnabledFlag && this.regionService.isRecordingRegionAvailable;
  }

  public get isRecordingOptionEnabled(): boolean {
    return this.isRecordingEnabledFlag && !this.isRecordingDisabled;
  }
}
